import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { selectFacilityProfile } from '../../modules/facility/selectors';
import { fetchFacilityProfile } from '../../modules/facility/actions';

import { useSidebar } from '../../context/SidebarContext';
import { useBreadcrumb } from '../../context/BreadcrumbContext';

import {
  HeaderRow,
  PageWrapper,
  PaginationFooter,
  StatusTag,
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  StyledTableContainer,
} from './styles';
import CustomTableHeader from '../../components/CustomHeader';
import CustomPagination from '../../components/CustomPagination';
import { Box, TableRow, Typography, IconButton } from '@mui/material';

import {
  fetchFacilityInvoices,
  fetchInvoiceDetails,
  selectInvoices,
  selectInvoicesCount,
  selectInvoicesLoading,
  selectInvoicesError,
  selectInvoiceDetails,
  selectInvoiceDetailsLoading,
} from '../../modules/invoice';

import InvoiceDetailsModal from '../../components/InvoiceDetailsModal';

import CustomDateRangePicker from '../../components/CustomDateRangePicker';
import { TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

type Order = 'asc' | 'desc';

interface Column {
  id: string;
  label: string;
  numeric?: boolean;
  disableSorting?: boolean;
}

const INVOICE_COLUMNS: Column[] = [
  { id: 'invoice_number', label: 'Invoice Number' },
  { id: 'period', label: 'Period' },
  { id: 'invoice_date', label: 'Issued Date' },
  { id: 'invoice_due_date', label: 'Due Date' },
  { id: 'invoice_amount', label: 'Amount' },
  { id: 'payment_status', label: 'Payment Status' },
];

const FacilityInvoice: React.FC = () => {
  const { setActivePage } = useSidebar();
  const { setIsShow } = useBreadcrumb();
  useEffect(() => {
    setActivePage('invoices');
    setIsShow(false);
  }, [setActivePage, setIsShow]);

  const dispatch = useDispatch<any>();
  const invoices = useSelector(selectInvoices);
  const invoicesCount = useSelector(selectInvoicesCount);
  const invoicesLoading = useSelector(selectInvoicesLoading);
  const invoicesError = useSelector(selectInvoicesError);

  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<string>(
    'invoice_id',
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const invoiceDetails = useSelector(selectInvoiceDetails);
  const invoiceDetailsLoading = useSelector(selectInvoiceDetailsLoading);

  const handleOpenDetails = (invoiceId: number) => {
    dispatch(fetchInvoiceDetails(invoiceId));
    setDetailsModalOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsModalOpen(false);
  };

  const facilityProfile = useSelector(selectFacilityProfile);
  useEffect(() => {
    dispatch(fetchFacilityProfile());
  }, [dispatch]);

  useEffect(() => {
    if (facilityProfile?.facility_id) {
      dispatch(
        fetchFacilityInvoices({
          page,
          pageSize: rowsPerPage,
          facilityId: facilityProfile.facility_id,
          search: searchTerm,
          startDate: dateRange[0]?.format('YYYY-MM-DD'),
          endDate: dateRange[1]?.format('YYYY-MM-DD'),
        }),
      );
    }
  }, [dispatch, page, rowsPerPage, searchTerm, dateRange, facilityProfile]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const propertyKey = property;

    const columnDef = INVOICE_COLUMNS.find((col) => col.id === property);
    if (columnDef?.disableSorting) {
      return;
    }

    const isAsc = orderBy === propertyKey && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(propertyKey);
  };

  console.log('invoices', invoices);

  return (
    <PageWrapper>
      <Box sx={{ width: '100%', padding: 3 }}>
        <HeaderRow>
          <Typography
            variant="h5"
            style={{ fontWeight: 'bold', color: '#222' }}
          >
            Facility Invoices
          </Typography>
          <Box display="flex" gap={2}>
            <TextField
              placeholder="Search..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear search"
                      onClick={() => setSearchTerm('')}
                      edge="end"
                      size="small"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <CustomDateRangePicker
              value={dateRange}
              onChange={(newDateRange) => setDateRange(newDateRange)}
            />

          </Box>
        </HeaderRow>
        <StyledTableContainer>
          <StyledTable stickyHeader>
            <CustomTableHeader
              columns={INVOICE_COLUMNS}
              order={order}
              orderBy={orderBy as string}
              onRequestSort={handleRequestSort}
            />
            <StyledTableBody>
              {invoicesLoading ? (
                <TableRow>
                  <StyledTableCell
                    colSpan={INVOICE_COLUMNS.length}
                    align="center"
                  >
                    Loading...
                  </StyledTableCell>
                </TableRow>
              ) : invoicesError ||
                !invoices ||
                !Array.isArray(invoices) ||
                invoices.length === 0 ? (
                <TableRow>
                  <StyledTableCell
                    colSpan={INVOICE_COLUMNS.length}
                    align="center"
                  >
                    No data available
                  </StyledTableCell>
                </TableRow>
              ) : (
                <>
                  {invoices.map((invoice: any) => (
                    <TableRow sx={{ cursor: 'pointer' }} key={invoice.invoice_id} hover onClick={() => handleOpenDetails(invoice.invoice_id)} >
                      <StyledTableCell>{invoice.invoice_number}</StyledTableCell>
                      <StyledTableCell>
                        {dayjs(invoice.period_start_from).format('DD/MM/YYYY')} - {dayjs(invoice.period_end_to).format('DD/MM/YYYY')}
                      </StyledTableCell>
                      <StyledTableCell>
                        {dayjs(invoice.invoice_date).format('DD/MM/YYYY')}
                      </StyledTableCell>
                      <StyledTableCell>
                        {dayjs(invoice.invoice_due_date).format('DD/MM/YYYY')}
                      </StyledTableCell>
                      <StyledTableCell>{invoice.invoice_amount}</StyledTableCell>
                      <StyledTableCell>
                        <StatusTag status={invoice.invoice_status}>
                          {invoice.payment_status}
                        </StatusTag>
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </>
              )}
            </StyledTableBody>
          </StyledTable>
        </StyledTableContainer>
        <PaginationFooter>
          <Box>
            Showing {invoices?.length || 0} of{' '}
            {invoicesCount} results
          </Box>
          <CustomPagination
            currentPage={page}
            totalPages={Math.ceil(invoicesCount / rowsPerPage)}
            onPageChange={(newPage) => setPage(newPage)}
          />
        </PaginationFooter>
      </Box>
      <InvoiceDetailsModal
        open={detailsModalOpen}
        onClose={handleCloseDetails}
        invoice={invoiceDetails}
        loading={invoiceDetailsLoading}
        facilityDetails={{
          name: facilityProfile?.facility_name || '',
          address: facilityProfile?.address || '',
        }}
      />
    </PageWrapper >
  );
};

export default FacilityInvoice;